import { Link, useRouterState } from "@tanstack/react-router";
import { Logo } from "./Logo";
import {
  LayoutDashboard, Search, Network, Clock, FileText, ShieldAlert,
  Radio, FolderOpen, Users, Settings, Database, Bot, Map, Shield, LogOut,
  BookOpen, Archive, Image as ImageIcon, Radar, Mail, UserSearch, Phone, Brain,
} from "lucide-react";
import { useAuth } from "@/lib/auth/AuthContext";

const NAV = [
  { group: "Operations", items: [
    { to: "/dashboard",    label: "Investigate",        icon: Search },
    { to: "/osint",        label: "OSINT Engines",      icon: Radar },
    { to: "/person",       label: "Person Intel",       icon: UserSearch },
    { to: "/email",        label: "Email Intel",        icon: Mail },
    { to: "/contacts",     label: "Contact Intel",      icon: Phone },
    { to: "/nexus",        label: "Nexus Orchestrator", icon: Brain },



    { to: "/investigate",  label: "Case Workspace",     icon: FolderOpen },
    { to: "/graph",        label: "Relationship Graph", icon: Network },
    { to: "/timeline",     label: "Timeline",           icon: Clock },
  ]},
  { group: "Intelligence", items: [
    { to: "/threats",      label: "Threat Center",   icon: ShieldAlert },
    { to: "/threat-simulation", label: "Threat Simulation", icon: Radar },
    { to: "/monitor",      label: "Live Monitoring", icon: Radio },
    { to: "/geo",          label: "GEOINT",          icon: Map },
    { to: "/imagery",      label: "Image Intel",     icon: ImageIcon },
    { to: "/sources",      label: "Source Registry", icon: Database },
  ]},
  { group: "Workspace", items: [
    { to: "/cases",        label: "Case Index", icon: LayoutDashboard },
    { to: "/vault",        label: "Scan Vault", icon: Archive },
    { to: "/reports",      label: "Reports",    icon: FileText },
    { to: "/team",         label: "Analysts",   icon: Users },
    { to: "/assistant",    label: "AI Analyst", icon: Bot },
    { to: "/guide",        label: "User Guide", icon: BookOpen },
    { to: "/settings",     label: "Settings",   icon: Settings },
  ]},
];

export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const path = useRouterState({ select: s => s.location.pathname });
  const { user, isAdmin, signOut } = useAuth();
  const initials = (user?.email ?? "??").slice(0, 2).toUpperCase();
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="px-4 py-4 border-b border-panel-border">
        <Link to="/" onClick={onNavigate}><Logo /></Link>
      </div>
      <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-5">
        {NAV.map(group => (
          <div key={group.group}>
            <div className="px-2 mb-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
              {group.group}
            </div>
            <ul className="space-y-0.5">
              {group.items.map(({ to, label, icon: Icon }) => {
                const active = path === to;
                return (
                  <li key={to}>
                    <Link to={to as "/dashboard"} onClick={onNavigate}
                      className={`group flex items-center gap-2.5 rounded-md px-2 py-2 text-[13px] transition-colors
                        ${active ? "bg-cyan/10 text-foreground ring-1 ring-cyan/30" : "text-muted-foreground hover:bg-white/5 hover:text-foreground"}`}>
                      <Icon className={`h-4 w-4 ${active ? "text-cyan" : ""}`} />
                      <span>{label}</span>
                      {active && <span className="ml-auto h-1 w-1 rounded-full bg-cyan pulse-dot" />}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {isAdmin && (
          <div>
            <div className="px-2 mb-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-amber">
              Administration
            </div>
            <ul className="space-y-0.5">
              <li>
                <Link to="/admin" onClick={onNavigate}
                  className={`group flex items-center gap-2.5 rounded-md px-2 py-2 text-[13px] transition-colors
                    ${path === "/admin" ? "bg-amber/10 text-foreground ring-1 ring-amber/30" : "text-muted-foreground hover:bg-white/5 hover:text-foreground"}`}>
                  <Shield className={`h-4 w-4 ${path === "/admin" ? "text-amber" : ""}`} />
                  <span>Control Room</span>
                </Link>
              </li>
            </ul>
          </div>
        )}
      </nav>
      <div className="border-t border-panel-border p-3">
        <div className="glass rounded-md p-2.5">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-full bg-gradient-to-br from-cyan to-[oklch(0.55_0.20_240)] grid place-items-center text-[10px] font-semibold text-background">{initials}</div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-medium">{user?.email ?? "Operator"}</div>
              <div className="font-mono text-[10px] text-muted-foreground">{isAdmin ? "CLEARANCE · ADMIN" : "CLEARANCE · ANALYST"}</div>
            </div>
            <button onClick={() => signOut()} title="Sign out" className="text-muted-foreground hover:text-danger">
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}